// Spreadsheet reading, on exceljs.
//
// Replaces xlsx (SheetJS), abandoned on npm at 0.18.5 with an unfixable
// prototype-pollution advisory (GHSA-4r6h-8v6p-xvw6).
//
// exceljs is loaded via dynamic import() so it is code-split out of the main
// bundle — it is several hundred KB and only three admin screens parse
// spreadsheets, so everyone else should not pay for it on first load.
//
// Keep in step with brand-equity-api/src/utils/workbook.ts.

// Cell value as plain text. exceljs returns a union, not a string: rich text,
// formulas, hyperlinks and errors are objects, so a naive String(value) yields
// "[object Object]" — data loss that looks like success.
const cellText = (value: any): string => {
    if (value === null || value === undefined) return '';

    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (value instanceof Date) return value.toISOString();

    if (typeof value === 'object') {
        if (Array.isArray(value.richText)) {
            return value.richText.map((r: any) => r?.text ?? '').join('');
        }
        if ('result' in value) return cellText(value.result);
        if ('formula' in value) return '';
        if ('text' in value) return cellText(value.text);
        if ('error' in value) return String(value.error);
    }

    return String(value);
};

// First sheet as objects keyed by the header row — equivalent to
// sheet_to_json({ defval: '' }). Takes an ArrayBuffer: callers must use
// FileReader.readAsArrayBuffer, not the readAsBinaryString the xlsx version
// consumed.
export const readFirstSheetAsObjects = async (
    data: ArrayBuffer,
): Promise<Record<string, string>[]> => {
    const ExcelJS = await import('exceljs');

    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(data);

    const sheet = workbook.worksheets[0];
    if (!sheet) return [];

    const width = sheet.columnCount;
    const raw: string[][] = [];

    // includeEmpty keeps blank rows so cells stay aligned with their headers.
    sheet.eachRow({ includeEmpty: true }, (row) => {
        const cells: string[] = [];
        for (let col = 1; col <= width; col++) {
            cells.push(cellText(row.getCell(col).value).trim());
        }
        raw.push(cells);
    });

    while (raw.length > 0 && raw[raw.length - 1].every((c) => c === '')) raw.pop();
    if (raw.length === 0) return [];

    const seen = new Map<string, number>();
    const headers = raw[0].map((h, i) => {
        const base = h || `column_${i + 1}`;
        const count = seen.get(base) ?? 0;
        seen.set(base, count + 1);
        return count === 0 ? base : `${base}_${count}`;
    });

    return raw.slice(1).map((cells) => {
        const record: Record<string, string> = {};
        headers.forEach((header, i) => {
            record[header] = cells[i] ?? '';
        });
        return record;
    });
};

// First sheet as arrays of arrays — equivalent to sheet_to_json({ header: 1 }).
export const readFirstSheetAsRows = async (
    data: ArrayBuffer,
): Promise<string[][]> => {
    const objects = await readFirstSheetAsObjects(data);
    if (objects.length === 0) return [];

    const headers = Object.keys(objects[0]);

    return [headers, ...objects.map((row) => headers.map((h) => row[h] ?? ''))];
};

// CSV cell, with the leading-formula guard. A value starting with = + - @ (or
// tab/CR) is executed as a formula when the file is opened in Excel or Sheets,
// so untrusted text in an export becomes code on someone else's machine.
// Prefixing an apostrophe neutralises it.
const csvCell = (value: unknown): string => {
    const text = value === null || value === undefined ? '' : String(value);
    const guarded = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;

    return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
};

// Build CSV from objects, header row taken from the union of keys.
export const toCsv = (rows: Record<string, unknown>[]): string => {
    const headers: string[] = [];
    for (const row of rows) {
        for (const key of Object.keys(row)) {
            if (!headers.includes(key)) headers.push(key);
        }
    }

    return [
        headers.map(csvCell).join(','),
        ...rows.map((row) => headers.map((h) => csvCell(row[h])).join(',')),
    ].join('\r\n');
};

// Build an .xlsx from arrays of arrays and download it. Rows are positional —
// the caller supplies its own header row if it wants one, matching what
// json_to_sheet({ skipHeader: true }) did.
export const downloadWorkbookFromRows = async (
    filename: string,
    sheets: { name: string; rows: unknown[][] }[],
): Promise<void> => {
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();

    for (const sheet of sheets) {
        // Excel rejects names over 31 chars or containing : \ / ? * [ ] —
        // xlsx tolerated them, exceljs throws.
        const name = (sheet.name || 'Sheet')
            .replace(/[:\\/?*[\]]/g, '-')
            .slice(0, 31);
        const ws = workbook.addWorksheet(name || 'Sheet');
        for (const row of sheet.rows) ws.addRow(row as any[]);
    }
    if (workbook.worksheets.length === 0) workbook.addWorksheet('Sheet1');

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// Trigger a browser download of text content.
export const downloadTextFile = (
    filename: string,
    content: string,
    mimeType = 'text/csv;charset=utf-8',
): void => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
